import i18n from 'i18next';
import {
  TEMPLATE_DEFECT_NAME_TO_ID,
  normalizeDefectNameKey,
} from 'common/constants/defectTypes';

function defectOptionKey(id) {
  return `taskTemplates.defectOptions.${id}`;
}

/** Translation in the active language only (avoids silent fallback to English). */
function translateCatalogDefect(t, catalogId) {
  const key = defectOptionKey(catalogId);
  const lng = (t.i18n && t.i18n.language) || i18n.language || 'en';
  const exists = (t.i18n && t.i18n.exists.bind(t.i18n)) || i18n.exists.bind(i18n);
  if (!exists(key, { lng, ns: 'translation' })) {
    return null;
  }
  return t(key, { lng, ns: 'translation' });
}

/**
 * Label for a global failure-mode / catalog defect id.
 * Keys: `taskTemplates.defectOptions.<id>` (see public/locales translation.json files).
 * Custom account failure modes (unknown id) fall back to `apiName`.
 *
 * @param {function} t - i18next `t`
 * @param {number|string|null|undefined} defectId
 * @param {string} [apiName] - API `name` when id has no catalog entry
 * @returns {string}
 */
export function getFailureModeDefectLabel(t, defectId, apiName) {
  const fallback = apiName != null && apiName !== '' ? String(apiName) : '';
  const n = Number(defectId);

  if (Number.isFinite(n) && n > 0) {
    const byId = translateCatalogDefect(t, n);
    if (byId != null) return byId;
  }

  if (fallback) {
    const catalogId = TEMPLATE_DEFECT_NAME_TO_ID[normalizeDefectNameKey(fallback)];
    if (catalogId != null) {
      const byName = translateCatalogDefect(t, catalogId);
      if (byName != null) return byName;
    }
  }

  return fallback;
}

/**
 * Label for a defect summary row: resolve id from failure-mode list when missing, then catalog label.
 *
 * @param {function} t - i18next `t`
 * @param {{ defectId?: number|string, defectName?: string, failureModesList?: Array<{id: number, name: string}> }} params
 * @returns {string}
 */
export function getDefectDisplayName(t, { defectId, defectName, failureModesList = [] } = {}) {
  let id = defectId;
  const apiName = defectName != null && defectName !== '' ? String(defectName) : '';
  const list = failureModesList || [];
  if ((id === undefined || id === null || id === '') && list.length && apiName) {
    const fm = list.find(f => f.name === apiName);
    if (fm) id = fm.id;
  }
  return getFailureModeDefectLabel(t, id, apiName);
}
